/* ────────────────────────────────────────────────────────────────────────
   sb-smoke-fill.js — Concept A smoke engine (white bg, multiply blend).

   Auto-mounts on every  canvas[data-sb-smoke-fill]  found in the DOM and
   inside shopify:section:load events.

   Canvas attributes read at mount time:
     data-field-selector   CSS selector (relative to the section) of the
                           element to protect with an invisible force field.
     data-fill-mode        "reveal"  (default on homepage hero) — fires a
                             'sb:filled' CustomEvent once ~140 puffs are
                             alive; the section can listen and reveal copy.
                           "ambient" — force field + cursor trail but no
                             fill threshold logic (used on collection heroes
                             where the title must always be visible).

   Perf / a11y:
     · DPR capped at 1.6
     · Skipped under prefers-reduced-motion
     · pointer-events tracked on the section element (canvas is none)
     · RAF pauses when section is offscreen (IntersectionObserver)
     · Hard cap of 300 puffs regardless of cursor velocity
──────────────────────────────────────────────────────────────────────── */
(function (window, document) {
  'use strict';

  var COLORS = [[229, 52, 47], [30, 114, 232], [47, 158, 68], [122, 66, 214], [255, 93, 160]];
  var MAX = 420;

  function mount(canvas) {
    if (!canvas || canvas.__sbSmokeFill) return;
    canvas.__sbSmokeFill = true;

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var section = canvas.closest('section') || canvas.parentElement;
    var fieldSel = canvas.getAttribute('data-field-selector');
    var fieldEl = fieldSel ? (section || document).querySelector(fieldSel) : null;
    var ambient = canvas.getAttribute('data-fill-mode') === 'ambient';

    /* persistent cursor dot — the hero hides the native pointer (cursor:none)
       and treats the smoke as the cursor, but smoke is suppressed over the
       headline, so without an always-on indicator the cursor "disappears".
       This color-synced dot follows the pointer everywhere, including over
       text, so it never gets lost. */
    var dot = document.createElement('div');
    dot.setAttribute('aria-hidden', 'true');
    /* Solid fill + white ring + soft shadow so it's visible on ANY background
       (white canvas, saturated smoke, or dark headline ink). No blend mode —
       blending over the constantly-repainting canvas was both invisible at
       times and a per-frame compositing cost (choppiness). */
    dot.style.cssText =
      'position:fixed;top:0;left:0;width:16px;height:16px;border-radius:50%;' +
      'pointer-events:none;z-index:9999;transform:translate(-50%,-50%);' +
      'opacity:0;transition:opacity .15s;will-change:transform;' +
      'box-shadow:0 0 0 2px rgba(255,255,255,.92), 0 1px 5px rgba(0,0,0,.35)';
    document.body.appendChild(dot);

    var puffs = [], colorT = 0, filledFired = false, visible = true;
    var mouse = { x: -999, y: -999, v: 0, seen: false };
    var dpr = 1, W = 0, H = 0;
    var field = { x: 0, y: 0, w: 0, h: 0, pad: 34 };
    var last = performance.now();

    /* ---- sizing ---- */
    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 1.6);
      W = canvas.clientWidth;
      H = canvas.clientHeight;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      measureField();
    }

    function measureField() {
      if (!fieldEl) return;
      var fr = fieldEl.getBoundingClientRect();
      var cr = canvas.getBoundingClientRect();
      field.x = fr.left - cr.left;
      field.y = fr.top - cr.top;
      field.w = fr.width;
      field.h = fr.height;
    }

    /* ---- color cycling ---- */
    function curColor() {
      var n = COLORS.length, f = (colorT % n + n) % n;
      var i = Math.floor(f), t = f - i;
      var a = COLORS[i], b = COLORS[(i + 1) % n];
      return [a[0] + (b[0] - a[0]) * t | 0, a[1] + (b[1] - a[1]) * t | 0, a[2] + (b[2] - a[2]) * t | 0];
    }

    /* ---- is a point inside the protected (padded) text rect? ---- */
    /* Used to suppress spawning puffs directly on the headline — otherwise
       puffs are born over the text and the force field instantly ejects them,
       producing a popcorn flicker right on the words. */
    function inField(x, y) {
      if (!fieldEl || field.w <= 0) return false;
      var fx = field.x - field.pad, fy = field.y - field.pad;
      var fw = field.w + field.pad * 2, fh = field.h + field.pad * 2;
      return x > fx && x < fx + fw && y > fy && y < fy + fh;
    }

    /* ---- spawn puffs ---- */
    function spawn(x, y, c, n, spread) {
      for (var i = 0; i < n; i++) {
        if (puffs.length >= MAX) break;
        var ang = Math.random() * Math.PI * 2, sp = Math.random() * spread;
        puffs.push({
          x: x, y: y,
          vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp - 0.14,
          r: 16 + Math.random() * 30,
          life: 1, decay: 0.0011 + Math.random() * 0.0015,
          c: c
        });
      }
    }

    /* ---- main frame ---- */
    function frame(t) {
      if (!visible) { requestAnimationFrame(frame); return; }

      var dt = Math.min((t - last) / 16.67, 3);
      last = t;
      colorT += 0.006;
      mouse.v *= 0.85;

      if (mouse.seen && mouse.x > -900) {
        var c = curColor();
        dot.style.background = 'rgb(' + c[0] + ',' + c[1] + ',' + c[2] + ')';
        /* suppress smoke over the headline (avoids popcorn flicker), but the
           dot above still shows the cursor there so it's never lost */
        if (!inField(mouse.x, mouse.y)) {
          var slow = mouse.v < 2.2;
          spawn(mouse.x, mouse.y, c, slow ? 7 : 4, slow ? 2.0 : 0.9);
        }
      }

      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'multiply';

      var alive = 0;
      for (var i = puffs.length - 1; i >= 0; i--) {
        var p = puffs[i];
        p.life -= p.decay;
        if (p.life <= 0) { puffs.splice(i, 1); continue; }
        alive++;

        /* turbulence + drift */
        p.vy += Math.sin((p.x + t * 0.02) * 0.01) * 0.01;
        p.vx *= 0.99; p.vy *= 0.99;
        p.x += p.vx * dt; p.y += p.vy * dt;
        p.r += 0.18 * dt;

        /* force field — smoothly push puffs out of the protected rect.
           Push scales with penetration depth (a soft spring) instead of a
           fixed velocity kick + positional snap, so puffs that drift in are
           eased out rather than violently teleported (no jitter). */
        if (fieldEl && field.w > 0) {
          var fx = field.x - field.pad, fy = field.y - field.pad;
          var fw = field.w + field.pad * 2, fh = field.h + field.pad * 2;
          if (p.x > fx && p.x < fx + fw && p.y > fy && p.y < fy + fh) {
            var cx2 = fx + fw / 2, cy2 = fy + fh / 2;
            var ddx = p.x - cx2, ddy = p.y - cy2;
            var ex = fw / 2 - Math.abs(ddx), ey = fh / 2 - Math.abs(ddy);
            /* push along the shallower axis (nearest edge), proportional to depth */
            if (ex < ey) { p.vx += (ddx < 0 ? -1 : 1) * ex * 0.018; }
            else         { p.vy += (ddy < 0 ? -1 : 1) * ey * 0.018; }
            p.life -= 0.004; /* bleed density at boundary so text stays legible */
          }
        }

        /* edge bounce — glass window effect */
        if (p.x < 0 || p.x > W) p.vx *= -0.6;
        if (p.y < 0 || p.y > H) p.vy *= -0.6;

        /* draw puff */
        var alpha = Math.min(p.life, 0.5) * 0.58;
        var g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
        g.addColorStop(0, 'rgba(' + p.c[0] + ',' + p.c[1] + ',' + p.c[2] + ',' + alpha + ')');
        g.addColorStop(1, 'rgba(' + p.c[0] + ',' + p.c[1] + ',' + p.c[2] + ',0)');
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
      }

      ctx.globalCompositeOperation = 'source-over';

      /* fill-reveal threshold (homepage hero only) */
      if (!ambient && !filledFired && alive >= 140) {
        filledFired = true;
        canvas.dispatchEvent(new CustomEvent('sb:filled', { bubbles: true }));
      }

      requestAnimationFrame(frame);
    }

    /* ---- pointer events on the section (canvas is pointer-events:none) ---- */
    var hitEl = section || canvas.parentElement;
    hitEl.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      var r = canvas.getBoundingClientRect();
      var x = e.clientX - r.left, y = e.clientY - r.top;
      mouse.v = Math.hypot(x - mouse.x, y - mouse.y);
      mouse.x = x; mouse.y = y; mouse.seen = true;
      /* dot uses viewport coords (position:fixed) so it tracks the real pointer */
      dot.style.transform = 'translate(' + e.clientX + 'px,' + e.clientY + 'px) translate(-50%,-50%)';
      dot.style.opacity = '1';
    }, { passive: true });

    hitEl.addEventListener('pointerleave', function () { mouse.x = -999; mouse.seen = false; dot.style.opacity = '0'; });

    /* ---- resize + field tracking ---- */
    window.addEventListener('resize', function () { resize(); });
    window.addEventListener('scroll', measureField, { passive: true });

    /* ---- pause when offscreen ---- */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { visible = es[0].isIntersecting; }, { threshold: 0.01 }).observe(canvas);
    }

    resize();
    requestAnimationFrame(frame);
  }

  function autoMount(root) {
    (root || document).querySelectorAll('canvas[data-sb-smoke-fill]').forEach(mount);
  }

  window.SBSmokeFill = { mount: mount, autoMount: autoMount };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { autoMount(); });
  } else {
    autoMount();
  }

  document.addEventListener('shopify:section:load', function (e) { autoMount(e.target); });
})(window, document);
